import { FC, useEffect, useReducer, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { addNegativeToast } from 'modules/toastContainer';
import noop, { NoOp } from 'utils/noop';
import { getFileNameFromUrl } from 'utils/stringHelpers';

import { useTranslations } from '../hooks/useTranslations';
import {
  Asset,
  ImageAttrBatch,
  ImageAttrState,
  ImageAttrs,
  ImageMetadata,
  PanelIntent,
  PanelView,
  UploadCompleteConfig,
} from '../types';
import { imageAttrReducer, imageAttrStateDefault } from '../utils/images';
import { PanelContext, PanelContextState } from './Panel';
import { useDebouncedEffect } from 'utils/hooks/useDebouncedEffect';
import { usePaginationContext } from 'utils/contexts/pagination';
import { useFilterContext } from './FilterContext';
import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';

export type Props = {
  initView?: PanelView;
  intent?: PanelIntent;
  onCancel?: NoOp;
  onClose?: NoOp;
  onInsert?: (attrs: Record<string, string>) => void;
  onSelect?: (url: string, imgMetadata?: ImageMetadata) => void;
  selectedEditorImageNode?: HTMLImageElement;
  selectedAssetData?: Asset;
  hideGifs?: boolean;
};

export const PanelContextProvider: FC<React.PropsWithChildren<Props>> = ({
  children,
  initView = PanelView.List,
  intent = PanelIntent.Browse,
  onCancel = noop,
  onClose = noop,
  onInsert,
  onSelect,
  selectedAssetData,
  selectedEditorImageNode,
  hideGifs = false,
}) => {
  const isCreativeLibraryAssetsFolderPanelFFEnabled = useFeatureFlag(
    'CreativeLibraryAssetsFolderPanel',
    false,
  );
  const { t } = useTranslations();
  const dispatchHook = useDispatch();
  const [providerHasInitiated, setProviderHasInitiated] = useState<boolean>(false);
  const { setCurrentPage } = usePaginationContext();
  const { searchQuery, setSearchQuery } = useFilterContext();
  const [panelSearch, setPanelSearch] = useState(searchQuery);
  const [panelIntent, setPanelIntent] = useState<PanelIntent>(intent);
  const initialIntentRef = useRef<PanelIntent>(intent);
  const [panelView, setPanelView] = useState<PanelView>(initView);
  const panelViewLive = useRef<PanelView>(initView);
  const [scrollBookmark, setScrollBookmark] = useState<number>(0);
  const [selectedAsset, setSelectedAsset] = useState<Asset | undefined>(selectedAssetData);
  const [selectedRemoteImageNode, setSelectedRemoteImageNode] = useState<HTMLImageElement>();
  const [imageAttrState, setImageAttrState] = useReducer(imageAttrReducer, imageAttrStateDefault);
  const [nonCustomizableAttrs, setNonCustomizableAttrs] = useState<Record<string, string>>({});
  const assetName =
    (selectedRemoteImageNode &&
      (getFileNameFromUrl(selectedRemoteImageNode.src || '') || t('label.unnamedImage'))) ||
    (selectedAsset && selectedAsset.assetName) ||
    (selectedEditorImageNode &&
      (getFileNameFromUrl(selectedEditorImageNode.getAttribute('src') || '') ||
        t('label.unnamedImage'))) ||
    t('label.unnamedImage');
  const assetUrl =
    selectedRemoteImageNode?.src ||
    selectedAsset?.url ||
    selectedEditorImageNode?.getAttribute('src') ||
    '';
  const imgMetadata: ImageMetadata = {
    assetName,
    height: 0,
    width: 0,
  };

  if (selectedRemoteImageNode) {
    imgMetadata.height = selectedRemoteImageNode.naturalHeight;
    imgMetadata.width = selectedRemoteImageNode.naturalWidth;
  } else if (selectedAsset) {
    imgMetadata.createdAt = selectedAsset.createdAt;
    imgMetadata.height = selectedAsset.height;
    imgMetadata.size = selectedAsset.size;
    imgMetadata.width = selectedAsset.width;
    imgMetadata.creatorName = selectedAsset.creatorUser?.fullName;
    imgMetadata.altText = selectedAsset.altText;
  } else if (selectedEditorImageNode) {
    imgMetadata.height = selectedEditorImageNode.naturalHeight;
    imgMetadata.width = selectedEditorImageNode.naturalWidth;
  }

  useDebouncedEffect(
    () => {
      setCurrentPage(1);
      setSearchQuery(panelSearch);
    },
    [panelSearch],
    {
      skipEffectOnMount: true,
      debounceTimeout: 500,
    },
  );

  const handleSetPanelView = (pv: PanelView) => {
    setPanelView(pv);
    panelViewLive.current = pv;
  };

  const handleSetSelectedAsset = (asset: Asset) => {
    // only one should exist at a time
    setSelectedRemoteImageNode(undefined);
    setSelectedAsset(asset);
  };

  const handleSetSelectedRemoteImageNode = (elt: HTMLImageElement) => {
    // only one should exist at a time
    setSelectedAsset(undefined);
    setSelectedRemoteImageNode(elt);
  };

  const handleNewFiles = (fileCount = 0) => {
    if (isCreativeLibraryAssetsFolderPanelFFEnabled) {
      if (panelIntent === PanelIntent.ImportFromUrl) {
        setPanelIntent(PanelIntent.Upload);
      }
      return;
    }

    if (fileCount === 1 && !selectedAsset && !selectedEditorImageNode && !selectedRemoteImageNode) {
      if (panelViewLive.current !== PanelView.Detail) {
        handleSetPanelView(PanelView.Detail);
      }
    } else if (fileCount && panelViewLive.current !== PanelView.Upload) {
      handleSetPanelView(PanelView.Upload);
    }
  };

  const handleUploadComplete = ({
    filesToDismiss,
    newFiles,
    removeSelectedUploads,
    setPendingImages,
    uploadFailures,
    uploadSuccesses,
  }: UploadCompleteConfig) => {
    const dismissIds = filesToDismiss.map((file) => file.id);
    if (uploadSuccesses.length) {
      // Files have been added, so reset the scroll bookmark back to the top so that subsequent page
      // fetches are not accidentally triggered
      setScrollBookmark(0);
      // auto-select the upload if only one was uploaded while on the detail view
      // (the drop zone on PanelView.Detail only accepts a single image)
      if (panelViewLive.current === PanelView.Detail) {
        handleSetSelectedAsset(uploadSuccesses[0].asset);
      }
    }

    if (panelViewLive.current !== PanelView.Upload) {
      // clear this batch of pending assets, but keep the errors if viewing detail so that a
      // single-file upload failure can be shown
      removeSelectedUploads([
        ...uploadSuccesses.map((us) => us.id),
        ...(panelViewLive.current === PanelView.Detail ? [] : uploadFailures.map(({ id }) => id)),
        ...(panelViewLive.current === PanelView.Detail ? [] : dismissIds),
      ]);
    } else if (uploadSuccesses.length) {
      // update the list of pending assets so that they can be clicked for details
      setPendingImages((pendingImgs) => {
        const newPendingImgs = [...pendingImgs];
        uploadSuccesses.forEach((upld) => {
          const idx = newPendingImgs.findIndex((pImg) => pImg.id === upld.id);
          if (idx > -1) {
            newPendingImgs[idx].asset = upld.asset;
            newPendingImgs[idx].isSelected = false; // not important here, but updating for consistency
          }
        });
        return newPendingImgs;
      });
    }
    // if the user has navigated back to the image library during upload, show toasts for any errors
    if (isCreativeLibraryAssetsFolderPanelFFEnabled || panelViewLive.current === PanelView.List) {
      // error toasts for files that failed processing (processImage) from STEP 2
      filesToDismiss.forEach((file) => {
        dispatchHook(
          addNegativeToast(
            `${t('errors.canNotAdd', { fileName: file.file.name })} ${
              file.validationError || t(`errors.processImage.${file.processError}`)
            }`,
          ),
        );
      });
      // error toasts for files that failed uploading
      uploadFailures.forEach(({ id }) => {
        // NOTE: the uploadError will not exist on this version of the failedFile, so show generic 'failed'
        const failedFile = newFiles.find((file) => file.id === id);
        if (failedFile) {
          dispatchHook(
            addNegativeToast(
              `${t('errors.canNotAdd', { fileName: failedFile.file.name })} ${t(
                `errors.uploadAsset.failed`,
              )}`,
            ),
          );
        }
      });
    }
    if (isCreativeLibraryAssetsFolderPanelFFEnabled) {
      setPanelView(PanelView.List);
      setPanelIntent(initialIntentRef.current);
    }
  };

  useEffect(() => {
    const attrValues = {} as ImageAttrState;
    if (selectedEditorImageNode) {
      const nodeHeight = (selectedEditorImageNode.getAttribute('height') || '').trim();
      const nodeWidth = (selectedEditorImageNode.getAttribute('width') || '').trim();
      attrValues[ImageAttrs.ALT_DESC] = (selectedEditorImageNode.getAttribute('alt') || '').trim();
      attrValues[ImageAttrs.HEIGHT] = parseInt(nodeHeight, 10)
        ? parseInt(nodeHeight, 10).toString()
        : ''; // correct nonInteger attribute values
      attrValues[ImageAttrs.SOURCE] = (selectedEditorImageNode.getAttribute('src') || '').trim();
      attrValues[ImageAttrs.STYLES] = (selectedEditorImageNode.getAttribute('style') || '').trim();
      attrValues[ImageAttrs.WIDTH] = parseInt(nodeWidth, 10)
        ? parseInt(nodeWidth, 10).toString()
        : ''; // correct nonInteger attribute values
      // set the attributes initial values based on the selected image element in the WYSIWYG editor
      setImageAttrState({ type: ImageAttrBatch.SET_NEW, value: attrValues });
    }
    setProviderHasInitiated(true);
  }, []);

  const context: PanelContextState = {
    assetUrl,
    hideGifs,
    imageAttrState,
    imgMetadata,
    nonCustomizableAttrs,
    onCancel,
    onClose,
    onInsert,
    onNewFiles: handleNewFiles,
    onSelect,
    onUploadComplete: handleUploadComplete,
    panelIntent,
    initialIntent: initialIntentRef.current,
    setPanelIntent,
    panelSearch,
    panelView,
    providerHasInitiated,
    scrollBookmark,
    selectedAsset,
    selectedEditorImageNode,
    selectedRemoteImageNode,
    setImageAttrState,
    setNonCustomizableAttrs,
    setPanelSearch,
    setPanelView: handleSetPanelView,
    setScrollBookmark,
    setSelectedAsset: handleSetSelectedAsset,
    setSelectedRemoteImageNode: handleSetSelectedRemoteImageNode,
  };

  return <PanelContext.Provider value={context}>{children}</PanelContext.Provider>;
};
